import React, { useRef, memo } from 'react';
import { useForm, useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import {
  BUILDING_TYPE_ID,
  FORM_VALUES
} from '../../../../../utils/helpers/constants';
import { getCategoryValues } from '../../../../../utils/helpers/common';
import { Select } from '../../../../ui/Select';
import { Category } from '../../../../../graphql/types';
import { NewRadioButton } from '../../../../NewRadioButton';
import { SelectRefType } from '../../../../NewSearch/NewSearchBar/types';
import {
  SelectContentContainer,
  selectPopoverStyles,
  selectStyles,
  GroupTitle,
  GroupTitleText,
  OptionGroup
} from '../styled';
import { generateDataStat } from '../../../../../utils/helpers/generateDataStat';
import useScreenSize from '../../../../../hooks/useScreenSize';
import { useClient } from '../../../../../hooks/useClient';

interface CategoriesSelectProps {
  categories: Category[];
}

const CategoriesSelectComponent: React.FC<CategoriesSelectProps> = ({
  categories = []
}) => {
  const { t } = useTranslation();
  const { change } = useForm();
  const { input: typeEstateField } = useField(FORM_VALUES.typeEstate, {
    subscription: { value: true }
  });
  const { input: categoryIdField } = useField(FORM_VALUES.categoryId, {
    subscription: { value: true }
  });
  const { isSmall } = useScreenSize();
  const isClient = useClient();
  const selectRef = useRef<SelectRefType>(null);

  const onCategoryHandleClick = ({ name, id }: Category) => {
    change(FORM_VALUES.typeEstate, name);
    const newCategoryId =
      id === BUILDING_TYPE_ID.FLAT_ALL ? BUILDING_TYPE_ID.FLAT_NEW : id;
    change(FORM_VALUES.categoryId, newCategoryId);
    change(FORM_VALUES.categoryValues, getCategoryValues({ id, categories }));
    if (selectRef.current) {
      selectRef.current?.closePopover();
    }
  };

  const placeholder = `${t('new_search.type_estate')} *`;
  const { value } = typeEstateField;
  const currentCategoryId = categoryIdField.value;

  return (
    <Select
      ref={selectRef}
      width="100%"
      value={value}
      placeholder={placeholder}
      cypress="category-dropdown"
      styles={selectStyles}
      modalTitle={placeholder}
      showModalHeaderBorder={false}
      popoverStyles={selectPopoverStyles}
      modalCypress="item-form-category"
      stat={generateDataStat('create_ad_step3_type', isSmall, isClient) || ''}
      isWithHover={false}
      isPlaceholderVisible
    >
      <SelectContentContainer>
        {categories.map(item => {
          const hasChildren =
            Array.isArray(item.children) && item.children.length > 0;

          if (hasChildren) {
            return (
              <OptionGroup key={item.id}>
                <GroupTitle>
                  <GroupTitleText tag="body_2">{item.name}</GroupTitleText>
                </GroupTitle>

                {item.children.map(child => (
                  <NewRadioButton
                    key={child.id}
                    data={child}
                    cypress="cat-item"
                    name={item?.slug}
                    currentValue={currentCategoryId}
                    onChangeHandler={() => onCategoryHandleClick(child)}
                    isSelected={child.id === currentCategoryId}
                  />
                ))}
              </OptionGroup>
            );
          }

          return (
            <NewRadioButton
              key={item.id}
              data={item}
              cypress="cat-item"
              currentValue={currentCategoryId}
              onChangeHandler={() => onCategoryHandleClick(item)}
              isSelected={item.id === currentCategoryId}
            />
          );
        })}
      </SelectContentContainer>
    </Select>
  );
};

export const CategoriesSelect = memo(CategoriesSelectComponent);
